import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import apiService from '../../services/api';
import { SubscriptionPlan } from '../../types';
import { DateInput } from '../../components/Forms';

interface FormData {
  name: string;
  companyName: string;
  contact: {
    phone: string;
    email: string;
    address: {
      street: string;
      city: string;
      state: string;
      country: string;
    };
  };
  license: {
    number: string;
    issuer: string;
    issueDate: string;
    expiryDate: string;
  };
  subscription: {
    planId: string;
    endDate: string;
  };
  adminUser: {
    username: string;
    fullName: string;
    password: string;
    email: string;
    phone: string;
  };
}

const VendorRegister: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState('');
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    companyName: '',
    contact: {
      phone: '',
      email: '',
      address: {
        street: '',
        city: '',
        state: '',
        country: 'Saudi Arabia'
      }
    },
    license: {
      number: '',
      issuer: '',
      issueDate: '',
      expiryDate: ''
    },
    subscription: {
      planId: '',
      endDate: ''
    },
    adminUser: {
      username: '',
      fullName: '',
      password: '',
      email: '',
      phone: ''
    }
  });

  // Fetch subscription plans on component mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setIsLoadingPlans(true);
        console.log('🔍 Fetching subscription plans...');
        const plans = await apiService.getSubscriptionPlans();
        console.log('✅ Subscription plans fetched:', plans);
        setSubscriptionPlans(plans);
      } catch (error) {
        console.error('❌ Error fetching subscription plans:', error);
        setError('خطأ في جلب خطط الاشتراك');
      } finally {
        setIsLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  // Update end date when plan changes
  useEffect(() => {
    if (selectedPlan) {
      const startDate = new Date();
      const endDate = new Date(startDate);
      
      if (selectedPlan.durationUnit === 'month') {
        endDate.setMonth(endDate.getMonth() + selectedPlan.duration);
      } else if (selectedPlan.durationUnit === 'year') {
        endDate.setFullYear(endDate.getFullYear() + selectedPlan.duration);
      }
      
      setFormData(prev => ({
        ...prev,
        subscription: {
          ...prev.subscription,
          planId: selectedPlan._id,
          endDate: endDate.toISOString().split('T')[0]
        }
      }));
    }
  }, [selectedPlan]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'subscription.planId') {
      const plan = subscriptionPlans.find(p => p._id === value);
      setSelectedPlan(plan || null);
      return;
    }

    if (name.includes('contact.address.')) {
      const field = name.split('contact.address.')[1];
      setFormData(prev => ({
        ...prev,
        contact: {
          ...prev.contact,
          address: {
            ...prev.contact.address,
            [field]: value
          }
        }
      }));
    } else if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof FormData] as any),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        if (!formData.name || !formData.companyName || !formData.subscription.planId) {
          console.log('Step 1 validation failed:', { 
            name: formData.name, 
            companyName: formData.companyName, 
            planId: formData.subscription.planId 
          });
          setError('يرجى ملء جميع الحقول المطلوبة');
          return false;
        }
        break;
      case 2:
        if (!formData.contact.phone || !formData.contact.email || !formData.contact.address.city) {
          console.log('Step 2 validation failed:', { 
            phone: formData.contact.phone, 
            email: formData.contact.email, 
            city: formData.contact.address.city 
          });
          setError('يرجى ملء جميع الحقول المطلوبة');
          return false;
        }
        break;
      case 3:
        if (!formData.license.number || !formData.license.issuer || !formData.license.issueDate || !formData.license.expiryDate) {
          console.log('Step 3 validation failed:', { 
            number: formData.license.number, 
            issuer: formData.license.issuer, 
            issueDate: formData.license.issueDate, 
            expiryDate: formData.license.expiryDate 
          });
          setError('يرجى ملء جميع الحقول المطلوبة');
          return false;
        }
        break;
      case 4:
        if (!formData.adminUser.username || !formData.adminUser.fullName || !formData.adminUser.password || !formData.adminUser.email || !formData.adminUser.phone) {
          console.log('Step 4 validation failed:', { 
            username: formData.adminUser.username, 
            fullName: formData.adminUser.fullName, 
            password: formData.adminUser.password, 
            email: formData.adminUser.email, 
            phone: formData.adminUser.phone 
          });
          setError('يرجى ملء جميع الحقول المطلوبة');
          return false;
        }
        break;
    }
    setError('');
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
  };

  const registerMutation = useMutation({
    mutationFn: (data: any) => apiService.registerVendor(data),
    onSuccess: () => {
      navigate('/vendor-register-success');
    },
    onError: (error: any) => {
      console.error('❌ Registration error:', error);
      // Clear any previous errors
      setError('');
      
      // Handle different types of errors
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Handle validation errors from backend
        const errorMessages = error.response.data.errors.map((err: any) => err.msg).join('\n');
        setError(errorMessages);
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.message) {
        setError(error.message);
      } else {
        setError('حدث خطأ أثناء التسجيل. يرجى المحاولة مرة أخرى.');
      }
    }
  });

  const handleSubmit = () => {
    // Clear any previous errors
    setError('');
    
    // Validate the current step first
    if (!validateStep(4)) {
      return; // Don't proceed if validation fails
    }

    // Prevent multiple submissions
    if (registerMutation.isPending) {
      console.log('⚠️ Registration already in progress...');
      return;
    }

    console.log('🚀 Starting vendor registration...');
    
    // Transform form data to match API expected structure
    const apiData = {
      vendor: {
        name: formData.name,
        companyName: formData.companyName,
        contact: formData.contact,
        address: formData.contact.address,
        license: {
          number: formData.license.number,
          issuingAuthority: formData.license.issuer,
          issueDate: formData.license.issueDate,
          expiryDate: formData.license.expiryDate
        },
        subscription: {
          planId: formData.subscription.planId,
          endDate: formData.subscription.endDate
        }
      },
      adminUser: formData.adminUser
    };
    
    console.log('📤 Sending registration data:', apiData);
    registerMutation.mutate(apiData);
  };

  const steps = [
    { title: 'المعلومات الأساسية', description: 'اسم البائع والشركة وخطة الاشتراك' },
    { title: 'معلومات الاتصال', description: 'الهاتف والبريد الإلكتروني والعنوان' },
    { title: 'معلومات الترخيص', description: 'تفاصيل الترخيص التجاري' },
    { title: 'المستخدم الإداري', description: 'إنشاء حساب المستخدم الإداري' }
  ];

  return (
    <div className="min-h-screen bg-parchment-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link
          to="/landing"
          className="inline-flex items-center gap-2 text-sm font-medium text-navy-500 transition-colors hover:text-copper-600"
        >
          <ArrowRightIcon className="h-4 w-4" />
          الرئيسية
        </Link>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-navy-900">
          تسجيل بائع جديد
        </h2>
        <p className="mt-2 text-center text-sm text-navy-500/80">
          انضم إلى منصة حجز الحج والعمرة
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-4xl">
        <div className="bg-white py-8 px-4 shadow-lg ring-1 ring-black/5 sm:rounded-2xl sm:px-10">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={index} className="flex items-center">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                    currentStep > index + 1 ? 'bg-copper-500' :
                    currentStep === index + 1 ? 'bg-navy-900' : 'bg-gray-300'
                  } text-white text-sm font-medium`}>
                    {currentStep > index + 1 ? '✓' : index + 1}
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm font-medium ${
                      currentStep >= index + 1 ? 'text-navy-900' : 'text-gray-500'
                    }`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-gray-500">{step.description}</p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-16 h-0.5 mx-4 ${
                      currentStep > index + 1 ? 'bg-copper-500' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="mr-3">
                  <h3 className="text-sm font-medium text-red-800 mb-2">
                    يرجى تصحيح الأخطاء التالية:
                  </h3>
                  <div className="text-sm text-red-700">
                    {error.split('\n').map((errorMsg, index) => (
                      <div key={index} className="mb-1">
                        • {errorMsg}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-navy-900 mb-4">المعلومات الأساسية</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    اسم البائع <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-copper-500 focus:border-copper-500"
                    placeholder="اسم البائع"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    اسم الشركة <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-copper-500 focus:border-copper-500"
                    placeholder="اسم الشركة"
                    required
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    خطة الاشتراك <span className="text-red-500">*</span>
                  </label>
                  
                  {isLoadingPlans ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-copper-500"></div>
                      <span className="mr-3 text-gray-600">جاري تحميل خطط الاشتراك...</span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {subscriptionPlans.map((plan) => (
                        <div
                          key={plan._id}
                          onClick={() => {
                            setSelectedPlan(plan);
                            setFormData(prev => ({
                              ...prev,
                              subscription: {
                                ...prev.subscription,
                                planId: plan._id
                              }
                            }));
                          }}
                          className={`relative cursor-pointer rounded-lg border-2 p-4 transition-all duration-200 ${
                            selectedPlan?._id === plan._id
                              ? 'border-copper-500 bg-copper-50 shadow-lg'
                              : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                          }`}
                        >
                          {/* Selected indicator */}
                          {selectedPlan?._id === plan._id && (
                            <div className="absolute top-2 right-2">
                              <div className="w-6 h-6 bg-copper-500 rounded-full flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            </div>
                          )}

                          {/* Plan Header */}
                          <div className="text-center mb-4">
                            <h3 className="text-lg font-semibold text-navy-900 mb-1">{plan.name}</h3>
                            <p className="text-sm text-gray-600">{plan.description}</p>
                          </div>

                          {/* Price */}
                          <div className="text-center mb-4">
                            <div className="text-2xl font-bold text-copper-600">
                              {plan.price} ريال
                            </div>
                            <div className="text-sm text-gray-500">
                              /{plan.duration} {plan.durationUnit === 'month' ? 'شهر' : 'سنة'}
                            </div>
                          </div>

                          {/* Limits */}
                          <div className="grid grid-cols-3 gap-2 mb-4 text-center text-sm">
                            <div>
                              <div className="font-semibold text-navy-900">
                                {plan.maxUsers === -1 ? '∞' : plan.maxUsers}
                              </div>
                              <div className="text-gray-500">مستخدم</div>
                            </div>
                            <div>
                              <div className="font-semibold text-navy-900">
                                {plan.maxBuses === -1 ? '∞' : plan.maxBuses}
                              </div>
                              <div className="text-gray-500">حافلة</div>
                            </div>
                            <div>
                              <div className="font-semibold text-navy-900">
                                {plan.maxTrips === -1 ? '∞' : plan.maxTrips}
                              </div>
                              <div className="text-gray-500">رحلة</div>
                            </div>
                          </div>

                          {/* Features */}
                          {plan.features && plan.features.length > 0 && (
                            <div>
                              <h4 className="font-medium text-navy-900 mb-2 text-sm">المميزات:</h4>
                              <ul className="space-y-1">
                                {plan.features.slice(0, 3).map((feature, index) => (
                                  <li key={index} className="flex items-center text-xs text-gray-600">
                                    <span className="text-green-500 mr-1">✓</span>
                                    {feature}
                                  </li>
                                ))}
                                {plan.features.length > 3 && (
                                  <li className="text-xs text-copper-600">
                                    +{plan.features.length - 3} ميزات أخرى
                                  </li>
                                )}
                              </ul>
                            </div>
                          )}

                          {/* Status Badge */}
                          <div className="mt-3 text-center">
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                plan.isActive
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {plan.isActive ? 'متاح' : 'غير متاح'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {!isLoadingPlans && !selectedPlan && subscriptionPlans.length > 0 && (
                    <p className="text-sm text-red-600 mt-2">يرجى اختيار خطة اشتراك</p>
                  )}
                </div>

                {selectedPlan && (
                  <div className="col-span-2 bg-parchment-50 p-4 rounded-lg">
                    <h4 className="font-medium text-navy-900 mb-2">{selectedPlan.name}</h4>
                    <p className="text-navy-500/80 text-sm mb-3">{selectedPlan.description}</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-navy-900">السعر:</span>
                        <span className="text-navy-500/80"> {selectedPlan.price} ريال/{selectedPlan.durationUnit === 'month' ? 'شهر' : 'سنة'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-navy-900">المدة:</span>
                        <span className="text-navy-500/80"> {selectedPlan.duration} {selectedPlan.durationUnit === 'month' ? 'شهر' : 'سنة'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-navy-900">تاريخ الانتهاء:</span>
                        <span className="text-navy-500/80"> {formData.subscription.endDate}</span>
                      </div>
                    </div>
                    {selectedPlan.features && selectedPlan.features.length > 0 && (
                      <div className="mt-3">
                        <span className="font-medium text-navy-900 text-sm">المميزات:</span>
                        <ul className="text-navy-500/80 text-sm mt-1 space-y-1">
                          {selectedPlan.features.map((feature: string, index: number) => (
                            <li key={index} className="flex items-center">
                              <span className="text-copper-500 mr-2">•</span>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Contact Information */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-navy-900 mb-4">معلومات الاتصال</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    رقم الهاتف <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="contact.phone"
                    value={formData.contact.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-copper-500 focus:border-copper-500"
                    placeholder="+966501234567"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    البريد الإلكتروني <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="contact.email"
                    value={formData.contact.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-copper-500 focus:border-copper-500"
                    placeholder="info@company.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    الشارع
                  </label>
                  <input
                    type="text"
                    name="contact.address.street"
                    value={formData.contact.address.street}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-copper-500 focus:border-copper-500"
                    placeholder="اسم الشارع"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    المدينة <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="contact.address.city"
                    value={formData.contact.address.city}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-copper-500 focus:border-copper-500"
                    placeholder="الرياض"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    المنطقة
                  </label>
                  <input
                    type="text"
                    name="contact.address.state"
                    value={formData.contact.address.state}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-copper-500 focus:border-copper-500"
                    placeholder="المنطقة"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    الدولة
                  </label>
                  <input
                    type="text"
                    name="contact.address.country"
                    value={formData.contact.address.country}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-copper-500 focus:border-copper-500"
                    placeholder="Saudi Arabia"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: License Information */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-navy-900 mb-4">معلومات الترخيص</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    رقم الترخيص التجاري <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="license.number"
                    value={formData.license.number}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-copper-500 focus:border-copper-500"
                    placeholder="رقم الترخيص"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    جهة الإصدار <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="license.issuer"
                    value={formData.license.issuer}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-copper-500 focus:border-copper-500"
                    placeholder="وزارة التجارة"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    تاريخ الإصدار <span className="text-red-500">*</span>
                  </label>
                  <DateInput
                    value={formData.license.issueDate}
                    onChange={(e) =>
                      handleChange({
                        target: { name: 'license.issueDate', value: e.target.value },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    تاريخ انتهاء الصلاحية <span className="text-red-500">*</span>
                  </label>
                  <DateInput
                    value={formData.license.expiryDate}
                    onChange={(e) =>
                      handleChange({
                        target: { name: 'license.expiryDate', value: e.target.value },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Admin User */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-navy-900 mb-4">المستخدم الإداري</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    اسم المستخدم <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="adminUser.username"
                    value={formData.adminUser.username}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-copper-500 focus:border-copper-500"
                    placeholder="اسم المستخدم"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    الاسم الكامل <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="adminUser.fullName"
                    value={formData.adminUser.fullName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-copper-500 focus:border-copper-500"
                    placeholder="الاسم الكامل"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    كلمة المرور <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    name="adminUser.password"
                    value={formData.adminUser.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-copper-500 focus:border-copper-500"
                    placeholder="كلمة المرور"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    البريد الإلكتروني <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="adminUser.email"
                    value={formData.adminUser.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-copper-500 focus:border-copper-500"
                    placeholder="admin@company.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    رقم الجوال <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="adminUser.phone"
                    value={formData.adminUser.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-copper-500 focus:border-copper-500"
                    placeholder="+966501234567"
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium ${
                currentStep === 1
                  ? 'bg-gray-100 cursor-not-allowed'
                  : 'bg-white hover:bg-gray-50'
              }`}
            >
              السابق
            </button>

            {currentStep < 4 ? (
              <button
                type="button"
                onClick={nextStep}
                className="px-6 py-3 bg-navy-900 text-white rounded-lg font-medium hover:bg-copper-500 hover:text-navy-900 transition-colors"
              >
                التالي
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={registerMutation.isPending}
                className={`px-6 py-3 rounded-lg font-bold transition-all duration-200 ${
                  registerMutation.isPending
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-copper-500 text-navy-900 hover:bg-copper-400'
                }`}
              >
                {registerMutation.isPending ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
                    جاري التسجيل...
                  </div>
                ) : (
                  'إكمال التسجيل'
                )}
              </button>
            )}
          </div>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              لديك حساب بالفعل؟{' '}
              <a href="/vendor-login" className="font-medium text-copper-600 hover:text-copper-700">
                تسجيل الدخول
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorRegister; 